import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    FaCheck, FaTimes, FaUser, FaUsers, FaEdit,
    FaChevronDown, FaChair,
} from 'react-icons/fa';
import GuestService from '../services/guestService';
import { refreshDataGuest } from '../store/guestSlice';
import LoadingForButton from './LoadingForButton';
import FamilyTablesView from './FamilyTablesView';

// ─── STEPS ───────────────────────────────────────────────────────────────────
const FAMILY_STEPS = {
    ATTENDANCE: 'attendance', // Korak 1: tko dolazi
    TABLE: 'table',           // Korak 2: odabir stola
    CONFIRMED: 'confirmed',   // Završni prikaz
};

const capitalizeWords = (str) =>
    str?.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const FamilyReservationFlow = ({
    guest,
    weddingInfo,
    tablesWithGuests,
    setShowNotification,
    setNotificationMessage,
    setErrorResponse,
    setFamilyShowInvitation
}) => {
    const dispatch = useDispatch();

    // ─── Inicijalni step ─────────────────────────────────────────────────────
    const getInitialStep = () => {
        if (guest.rsvpDate) return FAMILY_STEPS.CONFIRMED;
        return FAMILY_STEPS.ATTENDANCE;
    };

    const [step, setStep] = useState(getInitialStep());
    const [loading, setLoading] = useState(false);

    // Lokalni state za attending checkboxove (radni draft, dok se ne spremi)
    const [memberDrafts, setMemberDrafts] = useState(
        (guest.familyMembers || []).map(m => ({ ...m }))
    );

    // Modal za potvrdu "ne dolazimo"
    const [showNotAttendingConfirm, setShowNotAttendingConfirm] = useState(false);

    useEffect(() => {
        if (setFamilyShowInvitation) {
            // Razglednica se prikazuje na ATTENDANCE i CONFIRMED, ne na TABLE
            const showInvitation = step !== FAMILY_STEPS.TABLE;
            setFamilyShowInvitation(showInvitation);
        }
    }, [step, setFamilyShowInvitation]);

    // Sinkroniziraj drafts kad se guest promijeni (npr. iz socket updatea)
    useEffect(() => {
        setMemberDrafts((guest.familyMembers || []).map(m => ({ ...m })));
    }, [guest._id, guest.familyMembers]);

    // Automatski reset step kad se guest.rsvpDate promijeni
    useEffect(() => {
        if (guest.rsvpDate && step === FAMILY_STEPS.ATTENDANCE) {
            setStep(FAMILY_STEPS.CONFIRMED);
        }
    }, [guest.rsvpDate]);

    // ─── Helpers ─────────────────────────────────────────────────────────────
    const attendingCount = memberDrafts.filter(m => m.attending === true).length;
    const notAttendingCount = memberDrafts.filter(m => m.attending === false).length;
    const pendingCount = memberDrafts.filter(m => m.attending === null || m.attending === undefined).length;
    const allResponded = pendingCount === 0;
    const nobodyAttending = allResponded && attendingCount === 0;

    // Stvarno stanje iz backenda (ne draft)
    const actualAttendingCount = (guest.familyMembers || []).filter(m => m.attending === true).length;
    const actualNobodyAttending = guest.rsvpDate && actualAttendingCount === 0;

    // ─── Handlers ────────────────────────────────────────────────────────────
    const toggleMember = (index, value) => {
        setMemberDrafts(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], attending: value };
            return updated;
        });
    };

    const setAllMembers = (value) => {
        setMemberDrafts(prev => prev.map(m => ({ ...m, attending: value })));
    };

    const hasUnsavedChanges = () => {
        const originalMembers = guest.familyMembers || [];
        if (memberDrafts.length !== originalMembers.length) return true;

        for (let i = 0; i < memberDrafts.length; i++) {
            const draft = memberDrafts[i];
            const original = originalMembers[i];
            // Normaliziraj null/undefined
            const draftStatus = draft.attending === undefined ? null : draft.attending;
            const originalStatus = original.attending === undefined ? null : original.attending;
            if (draftStatus !== originalStatus) return true;
        }

        return false;
    };

    const handleSaveAttendance = () => {
        if (!allResponded) {
            setNotificationMessage('Molimo odgovorite za sve članove obitelji.');
            setShowNotification(true);
            setErrorResponse(true);
            setTimeout(() => { setShowNotification(false); setErrorResponse(false); }, 3000);
            return;
        }

        setLoading(true);
        const payload = {
            memberStatuses: memberDrafts.map(m => ({ attending: m.attending })),
        };

        GuestService.confirmFamilyAttendance(payload)
            .then(() => {
                dispatch(refreshDataGuest());
                setNotificationMessage('Odgovor spremljen.');
                setShowNotification(true);
                setTimeout(() => setShowNotification(false), 3000);
                setStep(FAMILY_STEPS.CONFIRMED);
                setLoading(false);
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || 'Greška pri spremanju odgovora.';
                setNotificationMessage(msg);
                setShowNotification(true);
                setErrorResponse(true);
                setTimeout(() => { setShowNotification(false); setErrorResponse(false); }, 4000);
                setLoading(false);
            });
    };

    const handleEveryoneNotAttending = () => {
        setShowNotAttendingConfirm(false);
        setLoading(true);

        const payload = {
            memberStatuses: memberDrafts.map(() => ({ attending: false })),
        };

        GuestService.confirmFamilyAttendance(payload)
            .then(() => {
                dispatch(refreshDataGuest());
                setNotificationMessage('Odgovor spremljen.');
                setShowNotification(true);
                setTimeout(() => setShowNotification(false), 3000);
                setStep(FAMILY_STEPS.CONFIRMED);
                setLoading(false);
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || 'Greška pri spremanju odgovora.';
                setNotificationMessage(msg);
                setShowNotification(true);
                setErrorResponse(true);
                setTimeout(() => { setShowNotification(false); setErrorResponse(false); }, 4000);
                setLoading(false);
            });
    };

    // ─── RENDER: ATTENDANCE ───────────────────────────────────────────────────
    const renderAttendance = () => (
        <div className="flex flex-col items-center px-4 pb-10 mt-2">
            <div className="w-full max-w-md flex flex-col gap-5">
                {/* Header */}
                <div className="text-center">
                    <h2 className="text-xl font-bold text-gray-800">
                        Tko iz obitelji dolazi?
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Označite za svakog člana dolazi li ili ne.
                    </p>
                </div>

                {/* Info napomena */}
                <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-3 text-sm text-blue-700">
                    <span className="mt-0.5 flex-shrink-0">ℹ️</span>
                    <span>
                        Obitelj dijeli jednu pozivnicu. Bilo tko od ukućana može potvrditi dolazak za sve. Molimo dogovorite se prije potvrde.
                    </span>
                </div>

                {/* Quick actions */}
                <div className="flex gap-2">
                    <button
                        onClick={() => setAllMembers(true)}
                        className="flex-1 py-2 px-3 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-xl hover:bg-green-100 transition"
                    >
                        ✓ Svi dolaze
                    </button>
                    <button
                        onClick={() => setShowNotAttendingConfirm(true)}
                        className="flex-1 py-2 px-3 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition"
                    >
                        ✗ Niko ne dolazi
                    </button>
                </div>

                {/* Lista članova */}
                <div className="flex flex-col gap-2">
                    {memberDrafts.map((member, idx) => (
                        <div
                            key={`${member.name}-${idx}`}
                            className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col gap-2"
                        >
                            <div className="flex items-center gap-2">
                                <FaUser className="text-gray-400 text-sm flex-shrink-0" />
                                <span className="text-sm font-medium capitalize text-gray-800 truncate">
                                    {capitalizeWords(member.name)}
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => toggleMember(idx, true)}
                                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition border ${member.attending === true
                                        ? 'bg-green-500 text-white border-green-500'
                                        : 'bg-white text-gray-600 border-gray-300 hover:bg-green-50 hover:border-green-300'
                                        }`}
                                >
                                    <FaCheck className="inline text-xs mr-1" /> Dolazi
                                </button>
                                <button
                                    onClick={() => toggleMember(idx, false)}
                                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition border ${member.attending === false
                                        ? 'bg-red-500 text-white border-red-500'
                                        : 'bg-white text-gray-600 border-gray-300 hover:bg-red-50 hover:border-red-300'
                                        }`}
                                >
                                    <FaTimes className="inline text-xs mr-1" /> Ne dolazi
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Summary */}
                {allResponded && (
                    <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm">
                        <p className="font-semibold text-rose-700 mb-1">Sažetak</p>
                        <p className="text-gray-700">
                            Dolazi <strong>{attendingCount}</strong> {attendingCount === 1 ? 'osoba' : attendingCount < 5 ? 'osobe' : 'osoba'}
                            {notAttendingCount > 0 && (
                                <>, ne dolazi <strong>{notAttendingCount}</strong></>
                            )}
                        </p>
                    </div>
                )}

                {!allResponded && pendingCount < memberDrafts.length && (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-center">
                        Molimo odgovorite za sve članove prije nastavka.
                    </p>
                )}

                {/* Akcije */}
                <div className="flex flex-col gap-3 mt-1">
                    <button
                        onClick={handleSaveAttendance}
                        disabled={loading || !allResponded || !hasUnsavedChanges()}
                        className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-semibold text-lg bg-gradient-to-r from-rose-500 to-pink-600 shadow-lg shadow-rose-500/30 hover:opacity-90 transition disabled:opacity-50"
                    >
                        {loading ? <LoadingForButton /> : <><FaCheck /> Spremi odgovor</>}
                    </button>

                    <button
                        onClick={() => setStep(FAMILY_STEPS.TABLE)}
                        className="w-full py-3 text-rose-500 text-sm underline hover:text-rose-700 transition text-center"
                    >
                        🪑 Pogledaj slobodne stolove
                    </button>

                    {guest.rsvpDate && (
                        <button
                            onClick={() => setStep(FAMILY_STEPS.CONFIRMED)}
                            className="w-full py-3 text-gray-500 hover:text-gray-700 transition text-center"
                        >
                            ← Natrag
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    // ─── RENDER: TABLE ────────────────────────────────────────────────────────
    const renderTable = () => (
        <div className="w-full">
            <div className="flex justify-center mb-4 px-4">
                <button
                    onClick={() => setStep(
                        guest.rsvpDate ? FAMILY_STEPS.CONFIRMED : FAMILY_STEPS.ATTENDANCE
                    )}
                    className="text-gray-500 hover:text-gray-700 transition"
                >
                    ← Natrag
                </button>
            </div>

            <p className="text-center text-gray-700 font-medium text-lg mb-2">
                👋 {capitalizeWords(guest.fullName)}
            </p>

            <FamilyTablesView
                tables={tablesWithGuests}
                wedding={weddingInfo}
                guest={guest}
                attendingCount={actualAttendingCount}
                setShowNotification={setShowNotification}
                setNotificationMessage={setNotificationMessage}
                setErrorResponse={setErrorResponse}
                dispatch={dispatch}
            />
        </div>
    );

    // ─── RENDER: CONFIRMED ────────────────────────────────────────────────────
    const renderConfirmed = () => {
        const tableName = tablesWithGuests?.find(t => t._id === guest.reservedTableId)?.name;
        const attendingMembers = (guest.familyMembers || []).filter(m => m.attending === true);
        const notAttendingMembers = (guest.familyMembers || []).filter(m => m.attending === false);

        // Slučaj: niko ne dolazi
        if (actualNobodyAttending) {
            return (
                <div className="flex flex-col items-center px-4 pb-10 mt-6">
                    <div className="w-full max-w-sm flex flex-col gap-4">
                        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 text-center">
                            <FaTimes className="text-gray-400 text-3xl mx-auto mb-2" />
                            <h2 className="text-lg font-bold text-gray-800 mb-1">
                                Niko iz obitelji ne dolazi
                            </h2>
                            <p className="text-gray-500 text-sm">Vaš odgovor je zabilježen.</p>
                        </div>
                        <button
                            onClick={() => setStep(FAMILY_STEPS.ATTENDANCE)}
                            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                        >
                            <FaEdit /> Promijeni odluku
                        </button>
                    </div>
                </div>
            );
        }

        // Standardni prikaz
        return (
            <div className="flex flex-col items-center px-4 pb-10 mt-6">
                <div className="w-full max-w-sm flex flex-col gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center">
                        <FaCheck className="text-green-500 text-3xl mx-auto mb-2" />
                        <h2 className="text-lg font-bold text-gray-800 mb-1">
                            Odgovor potvrđen!
                        </h2>
                        <p className="text-gray-500 text-sm">
                            Dolazi {attendingMembers.length} {attendingMembers.length === 1 ? 'član' : 'članova'} obitelji.
                        </p>

                        {/* Lista članova koji dolaze */}
                        {attendingMembers.length > 0 && (
                            <div className="text-gray-600 text-sm mt-3 flex flex-col gap-1 text-left bg-white rounded-xl p-3 border border-green-100">
                                <p className="text-xs font-semibold text-green-700 mb-1">Dolaze:</p>
                                {attendingMembers.map((m, idx) => (
                                    <p key={idx} className="flex items-center gap-2">
                                        <FaCheck className="text-green-500 text-xs flex-shrink-0" />
                                        <span className="capitalize">{capitalizeWords(m.name)}</span>
                                    </p>
                                ))}
                                {notAttendingMembers.length > 0 && (
                                    <>
                                        <p className="text-xs font-semibold text-red-600 mt-2 mb-1">Ne dolaze:</p>
                                        {notAttendingMembers.map((m, idx) => (
                                            <p key={idx} className="flex items-center gap-2 text-gray-400">
                                                <FaTimes className="text-red-400 text-xs flex-shrink-0" />
                                                <span className="capitalize">{capitalizeWords(m.name)}</span>
                                            </p>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}

                        {tableName && (
                            <>
                                <p className="text-green-600 font-medium text-sm mt-3">Stol rezerviran ✓</p>
                                <p className="text-gray-500 text-sm mt-1 capitalize">
                                    Stol: <strong>{tableName}</strong>
                                </p>
                            </>
                        )}
                    </div>

                    {!tableName && (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
                            <p className="text-amber-700 font-semibold text-sm">⚠️ Stol još nije odabran</p>
                            <p className="text-amber-600 text-xs mt-1">
                                Možete ga odabrati sada ili kasnije.
                            </p>
                        </div>
                    )}

                    <button
                        onClick={() => setStep(FAMILY_STEPS.TABLE)}
                        className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-semibold bg-gradient-to-r from-rose-500 to-pink-600 shadow-lg shadow-rose-500/30 hover:opacity-90 transition"
                    >
                        {tableName ? 'Promijeni stol / pogledaj raspored' : 'Odaberi stol'}
                    </button>

                    <button
                        onClick={() => setStep(FAMILY_STEPS.ATTENDANCE)}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                    >
                        <FaEdit /> Uredi odgovor
                    </button>
                </div>
            </div>
        );
    };

    // ─── MAIN RENDER ──────────────────────────────────────────────────────────
    return (
        <>
            {step === FAMILY_STEPS.ATTENDANCE && renderAttendance()}
            {step === FAMILY_STEPS.TABLE && renderTable()}
            {step === FAMILY_STEPS.CONFIRMED && renderConfirmed()}

            {/* Modal: svi ne dolaze */}
            {showNotAttendingConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
                    <div className="bg-white p-6 rounded-2xl shadow-lg w-full max-w-sm text-center space-y-4">
                        <FaTimes className="text-gray-400 text-3xl mx-auto" />
                        <h3 className="text-lg font-semibold text-gray-800">Nitko ne dolazi?</h3>
                        <p className="text-sm text-gray-500">
                            Označit ćete da nitko iz obitelji ne dolazi. Ako ste imali rezerviran stol, bit će oslobođen.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={handleEveryoneNotAttending}
                                disabled={loading}
                                className="flex-1 bg-red-500 text-white py-3 rounded-xl font-medium hover:bg-red-600 transition"
                            >
                                {loading ? <LoadingForButton /> : 'Potvrdi'}
                            </button>
                            <button
                                onClick={() => setShowNotAttendingConfirm(false)}
                                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition"
                            >
                                Odustani
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FamilyReservationFlow;
